"use client";

import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  MapPin,
  Briefcase,
  Building2,
  DollarSign,
  Upload,
  ArrowRight,
  CheckCircle2
} from "lucide-react";
import ayraLogoDark from "@/assets/ayra-logo-dark.png";
import { getJobBySlug, JobData } from "@/data/jobListings";

// Animation variants
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

// Helper to parse bold text
const parseText = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, index) =>
    index % 2 === 1 ? <strong key={index}>{part}</strong> : part
  );
};

// Sidebar Info Component
const JobInfoSidebar = ({ job }: { job: JobData }) => (
  <div className="lg:sticky lg:top-28 z-10">
    <div className="bg-zinc-900/60 border border-white/[0.08] rounded-2xl p-6 space-y-5">
      <div className="flex items-start gap-3">
        <MapPin className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-zinc-500">Location</p>
          <p className="font-medium text-white">{job.location}</p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Briefcase className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-zinc-500">Employment Type</p>
          <p className="font-medium text-white">{job.employmentType}</p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <MapPin className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-zinc-500">Location Type</p>
          <p className="font-medium text-white">{job.locationType}</p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <Building2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-zinc-500">Department</p>
          <p className="font-medium text-white">{job.department}</p>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <DollarSign className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-zinc-500">Compensation</p>
          <p className="font-medium text-white">{job.compensation}</p>
        </div>
      </div>
    </div>
  </div>
);

// Overview Content
const OverviewContent = ({ job }: { job: JobData }) => (
  <motion.div
    initial="hidden"
    animate="visible"
    variants={staggerContainer}
    className="space-y-10"
  >
    {/* About Ayra */}
    <motion.section variants={staggerItem}>
      <h3 className="text-xl font-semibold text-white mb-4">Ayra AI</h3>
      <div className="space-y-3 text-zinc-400 body-text">
        {job.intro.map((paragraph, idx) => (
          <p key={idx}>{paragraph}</p>
        ))}
      </div>
      <a
        href="/call"
        className="inline-flex items-center gap-2 mt-4 text-emerald-400 font-medium hover:underline"
      >
        Try talking to Ayra
        <ArrowRight className="h-4 w-4" />
      </a>
    </motion.section>

    {/* Why We're Hiring / Why Join Us / Our Culture */}
    <motion.section variants={staggerItem}>
      <h3 className="text-xl font-semibold text-white mb-4">
        {job.slug === 'growth-marketing' ? 'Why Join Us?' : job.slug === 'account-executive' ? 'Our Culture' : 'Why We\'re Hiring This Role'}
      </h3>
      <div className="space-y-3 text-zinc-400 body-text">
        {job.whyHiring.paragraphs.map((paragraph, idx) => (
          <p key={idx}>{parseText(paragraph)}</p>
        ))}
        {job.whyHiring.emphasis && (
          <p className="font-medium text-white">{job.whyHiring.emphasis}</p>
        )}
      </div>
    </motion.section>

    {/* What You'll Do */}
    <motion.section variants={staggerItem}>
      <h3 className="text-xl font-semibold text-white mb-4">What You'll Do</h3>
      {job.whatYoullDo.sections ? (
        // Sections format for roles like Growth Marketing
        <div className="space-y-6">
          {job.whatYoullDo.sections.map((section, idx) => (
            <div key={idx} className="space-y-3">
              <h4 className="font-semibold text-white">{section.title}</h4>
              <ul className="space-y-2 text-zinc-400 body-text">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="flex items-start gap-3">
                    <span className="text-emerald-400 mt-1.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        // 30/60/90 day format
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-white/10">
            <p className="font-semibold text-white mb-1">30 Days</p>
            <p className="text-zinc-400 text-sm">{job.whatYoullDo.day30}</p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-white/10">
            <p className="font-semibold text-white mb-1">60 Days</p>
            <p className="text-zinc-400 text-sm">{job.whatYoullDo.day60}</p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-white/10">
            <p className="font-semibold text-white mb-1">90 Days</p>
            <p className="text-zinc-400 text-sm">{job.whatYoullDo.day90}</p>
          </div>
        </div>
      )}
    </motion.section>

    {/* Core Responsibilities (for roles that have both 30/60/90 and responsibilities) */}
    {job.coreResponsibilities && (
      <motion.section variants={staggerItem}>
        <h3 className="text-xl font-semibold text-white mb-4">Your Core Responsibilities</h3>
        <ul className="space-y-3 text-zinc-400 body-text">
          {job.coreResponsibilities.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <span className="text-emerald-400 mt-1.5">•</span>
              <span>{parseText(item)}</span>
            </li>
          ))}
        </ul>
      </motion.section>
    )}

    {/* Who You Are */}
    <motion.section variants={staggerItem}>
      <h3 className="text-xl font-semibold text-white mb-4">Who You Are</h3>
      <ul className="space-y-3 text-zinc-400 body-text">
        {job.whoYouAre.map((item, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
            <span>{parseText(item)}</span>
          </li>
        ))}
      </ul>
    </motion.section>

    {/* Why Ayra */}
    <motion.section variants={staggerItem}>
      <h3 className="text-xl font-semibold text-white mb-4">Why Ayra</h3>
      <ul className="space-y-3 text-zinc-400 body-text">
        {job.whyAyra.map((item, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <span className="text-emerald-400 font-semibold">{item.label}</span>
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    </motion.section>

    {/* What We Offer */}
    <motion.section variants={staggerItem}>
      <h3 className="text-xl font-semibold text-white mb-4">What We Offer</h3>
      <ul className="space-y-3 text-zinc-400 body-text">
        {job.whatWeOffer.map((item, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
            <span>{parseText(item)}</span>
          </li>
        ))}
      </ul>
    </motion.section>

    {/* CTA Button */}
    <motion.div variants={staggerItem}>
      <Button
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-400-foreground py-6 text-base font-semibold"
        onClick={() => document.getElementById('application-tab')?.click()}
      >
        Apply for this Job
        <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </motion.div>
  </motion.div>
);

// Application Form
const ApplicationForm = ({ job }: { job: JobData }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    portfolio: '',
    github: '',
    socialHandle: '',
    location: '',
    whyAyra: '',
    bestContent: ''
  });
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResumeFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-16"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="h-8 w-8 text-emerald-400" />
        </div>
        <h3 className="text-2xl font-semibold text-white mb-3">Application Submitted!</h3>
        <p className="text-zinc-400 max-w-md mx-auto">
          Thanks for applying to join Ayra. We'll review your application and get back to you soon.
        </p>
      </motion.div>
    );
  }

  const isEngineering = job.formType === 'engineering';
  const isMarketing = job.formType === 'marketing';
  const isCustomerFacing = job.formType === 'customer-facing';

  return (
    <motion.form
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* Resume Upload for Autofill */}
      <motion.div variants={staggerItem} className="p-5 rounded-xl bg-zinc-900/60 border border-white/10">
        <div className="flex items-start gap-3 mb-3">
          <Upload className="h-5 w-5 text-emerald-400 mt-0.5" />
          <div>
            <p className="font-medium text-white">Autofill from resume</p>
            <p className="text-sm text-zinc-400">Upload your resume here to autofill key application fields.</p>
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx"
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
        >
          <Upload className="mr-2 h-4 w-4" />
          {resumeFile ? resumeFile.name : 'Upload file'}
        </Button>
      </motion.div>

      {/* Basic Info */}
      <motion.div variants={staggerItem} className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Your full name"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="you@example.com"
            required
          />
        </div>
      </motion.div>

      <motion.div variants={staggerItem} className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="+1 (555) 000-0000"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="linkedin">LinkedIn *</Label>
          <Input
            id="linkedin"
            name="linkedin"
            type="url"
            value={formData.linkedin}
            onChange={handleInputChange}
            placeholder="https://linkedin.com/in/you"
            required
          />
        </div>
      </motion.div>

      {/* Role-specific fields */}
      {isEngineering && (
        <motion.div variants={staggerItem} className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="github">GitHub Link *</Label>
            <Input
              id="github"
              name="github"
              type="url"
              value={formData.github}
              onChange={handleInputChange}
              placeholder="https://github.com/you"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="portfolio">Portfolio (optional)</Label>
            <Input
              id="portfolio"
              name="portfolio"
              type="url"
              value={formData.portfolio}
              onChange={handleInputChange}
              placeholder="https://yourportfolio.com"
            />
          </div>
        </motion.div>
      )}

      {isMarketing && (
        <motion.div variants={staggerItem} className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="portfolio">Portfolio / Content Link *</Label>
            <Input
              id="portfolio"
              name="portfolio"
              type="url"
              value={formData.portfolio}
              onChange={handleInputChange}
              placeholder="https://yourportfolio.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="socialHandle">TikTok or Instagram Handle *</Label>
            <Input
              id="socialHandle"
              name="socialHandle"
              value={formData.socialHandle}
              onChange={handleInputChange}
              placeholder="@yourhandle"
              required
            />
          </div>
        </motion.div>
      )}

      {/* Location */}
      <motion.div variants={staggerItem} className="space-y-2">
        <Label htmlFor="location">Location *</Label>
        <Select onValueChange={(value) => setFormData({ ...formData, location: value })} required>
          <SelectTrigger>
            <SelectValue placeholder="Select your location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="canada">Canada</SelectItem>
            <SelectItem value="usa">United States</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Resume */}
      <motion.div variants={staggerItem} className="space-y-2">
        <Label htmlFor="resume">Resume *</Label>
        <Input
          id="resume"
          type="file"
          accept=".pdf,.doc,.docx"
          required
          className="cursor-pointer"
        />
      </motion.div>

      {/* Why Ayra */}
      <motion.div variants={staggerItem} className="space-y-2">
        <Label htmlFor="whyAyra">Why do you want to work at Ayra? *</Label>
        <Textarea
          id="whyAyra"
          name="whyAyra"
          value={formData.whyAyra}
          onChange={handleInputChange}
          placeholder="Tell us why you're excited about this role and Ayra..."
          rows={4}
          required
        />
      </motion.div>

      {/* Marketing-specific: Best content link or custom question */}
      {isMarketing && (
        <motion.div variants={staggerItem} className="space-y-2">
          <Label htmlFor="bestContent">
            {job.customFormQuestion?.label || "Send us your best piece of content (link) *"}
          </Label>
          {job.customFormQuestion ? (
            <Textarea
              id="bestContent"
              name="bestContent"
              value={formData.bestContent}
              onChange={handleInputChange}
              placeholder={job.customFormQuestion.placeholder}
              rows={3}
              required
            />
          ) : (
            <Input
              id="bestContent"
              name="bestContent"
              type="url"
              value={formData.bestContent}
              onChange={handleInputChange}
              placeholder="https://tiktok.com/@you/video/..."
              required
            />
          )}
        </motion.div>
      )}

      <motion.div variants={staggerItem}>
        <Button
          type="submit"
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-400-foreground py-6 text-base font-semibold"
        >
          Submit Application
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </motion.div>
    </motion.form>
  );
};

// 404 State
const JobNotFound = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black relative">
      {/* Global Grid Lines */}
      <div className="fixed inset-0 pointer-events-none z-[1]">
        <div className="absolute left-[5%] md:left-[8%] top-0 bottom-0 w-px bg-white/[0.15]" />
        <div className="absolute right-[5%] md:right-[8%] top-0 bottom-0 w-px bg-white/[0.15]" />
      </div>

      <Header />
      <main className="pt-24 pb-20 relative z-[2]">
        <div className="container px-4 text-center py-20">
          <h1 className="text-3xl font-bold text-white mb-4">Job Not Found</h1>
          <p className="text-zinc-400 mb-8">The position you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/careers')} className="bg-emerald-500 hover:bg-emerald-600 text-white">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Careers
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Main Job Detail Page
const JobDetailPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'application'>('overview');

  const job = slug ? getJobBySlug(slug) : undefined;

  if (!job) {
    return <JobNotFound />;
  }

  return (
    <div className="min-h-screen bg-black relative">
      {/* Global Grid Lines */}
      <div className="fixed inset-0 pointer-events-none z-[1]">
        <div className="absolute left-[5%] md:left-[8%] top-0 bottom-0 w-px bg-white/[0.15]" />
        <div className="absolute right-[5%] md:right-[8%] top-0 bottom-0 w-px bg-white/[0.15]" />
      </div>

      <Header />

      <main className="pt-24 pb-20 relative z-[2]">
        <div className="container px-4">
          {/* Back Button */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/careers')}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">Back to Careers</span>
            </button>

            <img
              src={ayraLogoDark}
              alt="Ayra"
              className="h-8 hidden md:block"
            />
          </div>

          {/* Job Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              {job.title}
            </h1>
          </motion.div>

          {/* Tabs */}
          <div className="flex justify-center gap-8 mb-12 border-b border-white/10">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-2 text-base font-medium transition-colors relative ${activeTab === 'overview'
                ? 'text-emerald-400'
                : 'text-zinc-400 hover:text-white'
                }`}
            >
              Overview
              {activeTab === 'overview' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-400"
                />
              )}
            </button>
            <button
              id="application-tab"
              onClick={() => setActiveTab('application')}
              className={`pb-4 px-2 text-base font-medium transition-colors relative ${activeTab === 'application'
                ? 'text-emerald-400'
                : 'text-zinc-400 hover:text-white'
                }`}
            >
              Application
              {activeTab === 'application' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-400"
                />
              )}
            </button>
          </div>

          {/* Two-column layout */}
          <div className="grid lg:grid-cols-[280px_1fr] gap-12 max-w-5xl mx-auto">
            {/* Sidebar - becomes top section on mobile */}
            <div className="order-2 lg:order-1">
              <JobInfoSidebar job={job} />
            </div>

            {/* Main Content */}
            <div className="order-1 lg:order-2">
              <AnimatePresence mode="wait">
                {activeTab === 'overview' ? (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <OverviewContent job={job} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="application"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ApplicationForm job={job} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default JobDetailPage;
